import { getUserFriendlyErrorMessage } from './errorMessages';

const API_BASE_URL = process.env.REACT_APP_API_URL;

// Validate that API_BASE_URL is configured
if (!API_BASE_URL) {
  throw new Error(
    'API configuration missing. REACT_APP_API_URL environment variable is not set. ' +
    'Please run "./scripts/fetch-ssm-config.sh <environment>" or check your .env file.'
  );
}

const POLLING_TIMEOUT_MS = 3600000; // 60 minutes (document ingestion)
const REPORT_POLLING_TIMEOUT_MS = 10800000; // 180 minutes (batch inference jobs can take 90+ min in queue + processing)

// Dynamic polling intervals with exponential backoff
// Start fast for document creation, then slow down for processing
const getPollingIntervalForDocument = (attemptNumber) => {
  if (attemptNumber < 10) return 500;    // First 10 attempts: 500ms (total ~5s)
  if (attemptNumber < 20) return 1000;   // Next 10 attempts: 1s (total ~10s)
  if (attemptNumber < 40) return 2000;   // Next 20 attempts: 2s (total ~40s)
  if (attemptNumber < 60) return 5000;   // Next 20 attempts: 5s (total ~100s)
  return 10000;                          // Remaining: 10s (for long processing)
};

class APIError extends Error {
  constructor(message, status, data) {
    super(message);
    this.name = 'APIError';
    this.status = status;
    this.data = data;
    this.userMessage = getUserFriendlyErrorMessage({ status, message, data });
  }
}

// Request upload URL with duplicate detection
// PC-193: now sends SHA-256 `fileHash`; backend accepts both `fileHash` and
// legacy `md5Hash` so rolling deploys work correctly.
export async function requestUploadURL(fetchAPI, file, fileHash) {
  const response = await fetchAPI('/api/documents/upload-url', {
    method: 'POST',
    body: JSON.stringify({
      filename: file.name,
      contentType: file.type,
      fileSize: file.size,
      fileHash,
    }),
  });

  return { ...response, fileHash };
}

// Upload file to S3 using pre-signed URL
// Includes metadata headers required by backend for duplicate detection
export async function uploadToS3(uploadUrl, file, uploadFields = {}) {
  // Use FormData for multipart/form-data POST (required by presigned POST)
  const formData = new FormData();

  // Add all signed fields from the presigned POST response
  // These include the policy, signature, and metadata fields
  for (const [key, value] of Object.entries(uploadFields)) {
    formData.append(key, value);
  }

  // Add the actual file LAST (S3 requirement)
  formData.append('file', file);

  console.log('[API] Uploading to S3 via presigned POST:', {
    filename: file.name,
    fields: Object.keys(uploadFields),
    hasMetadata: Object.keys(uploadFields).some(k => k.startsWith('x-amz-meta-'))
  });

  const response = await fetch(uploadUrl, {
    method: 'POST',
    body: formData,
    // Do NOT set Content-Type header - browser will set it with boundary parameter
  });

  if (!response.ok) {
    const errorText = await response.text().catch(() => 'Unknown error');
    throw new Error(`S3 upload failed: ${response.statusText}. ${errorText}`);
  }

  return true;
}

// Poll document status until processed with exponential backoff
export async function pollDocumentStatus(fetchAPI, documentId) {
  return new Promise((resolve, reject) => {
    let timeoutId;
    let attemptNumber = 0;

    const checkStatus = async () => {
      try {
        const data = await fetchAPI(`/api/documents/${documentId}`);
        const kbStatus = data.kb_status || data.kbStatus;
        const kbFailureReason = data.kb_failure_reason || data.kbFailureReason;

        const isProcessed = data.status === 'processed' && (!kbStatus || kbStatus === 'ready');
        const isKbFailed = data.status === 'kb_failed' || kbStatus === 'failed';

        if (isProcessed) {
          clearTimeout(timeoutId);
          resolve(data);
        } else if (data.status === 'failed' || isKbFailed) {
          clearTimeout(timeoutId);
          reject(new Error(
            kbFailureReason ||
            data.error ||
            'Document processing failed'
          ));
        } else {
          // Continue polling with exponential backoff
          attemptNumber++;
          const nextInterval = getPollingIntervalForDocument(attemptNumber);
          setTimeout(checkStatus, nextInterval);
        }
      } catch (error) {
        // Handle 404 errors gracefully - document may not exist yet during initial S3 upload + Lambda processing
        // This is expected and we should continue polling
        if (error.status === 404) {
          console.log(`Document ${documentId} not found yet, continuing to poll...`);
          attemptNumber++;
          const nextInterval = getPollingIntervalForDocument(attemptNumber);
          setTimeout(checkStatus, nextInterval);
          return;
        }

        // For other errors, stop polling and reject
        clearTimeout(timeoutId);
        reject(error);
      }
    };

    // Cleanup after timeout
    timeoutId = setTimeout(() => {
      reject(new Error('Document processing timeout'));
    }, POLLING_TIMEOUT_MS);

    // Start polling immediately
    checkStatus();
  });
}

// Library: Delete a document
function normalizeDocumentDeletionParams(documentInput) {
  if (!documentInput) {
    throw new Error('Document information is required to delete a document');
  }

  if (typeof documentInput === 'string') {
    return { documentId: documentInput };
  }

  const documentId = documentInput.documentId
    || documentInput.document_id
    || documentInput.id;

  if (!documentId) {
    throw new Error('Document ID is required to delete a document');
  }

  return {
    documentId,
    orgId: documentInput.orgId || documentInput.org_id,
    policyId: documentInput.policyId || documentInput.policy_id
  };
}

function buildDeleteDocumentRequest(documentInput) {
  const { documentId, orgId, policyId } = normalizeDocumentDeletionParams(documentInput);
  const payload = {};

  if (orgId) {
    payload.org_id = orgId;
  }
  if (policyId) {
    payload.policy_id = policyId;
  }

  return {
    documentId,
    body: Object.keys(payload).length > 0 ? JSON.stringify(payload) : undefined
  };
}

export async function deleteDocument(fetchAPI, documentInput) {
  const { documentId, body } = buildDeleteDocumentRequest(documentInput);

  return fetchAPI(`/api/documents/${documentId}`, {
    method: 'DELETE',
    body
  });
}

// Library: Get document deletion status (internal)
async function getDocumentDeletionStatus(fetchAPI, documentId, { kbSyncRequestId, kbJobId } = {}) {
  const params = new URLSearchParams();

  if (kbSyncRequestId) {
    params.append('kb_sync_request_id', kbSyncRequestId);
  } else if (kbJobId) {
    // Legacy fallback for older jobs
    params.append('kb_job_id', kbJobId);
  }

  const queryString = params.toString();
  const endpoint = queryString
    ? `/api/documents/${documentId}/deletion-status?${queryString}`
    : `/api/documents/${documentId}/deletion-status`;

  return fetchAPI(endpoint);
}

function resolveKbSyncId(result, fallbackId) {
  return result?.kb_sync_request_id || result?.kb_job_id || fallbackId;
}

function getPollingInterval(attempts) {
  return Math.min(5000 * Math.pow(1.5, Math.floor(attempts / 10)), 30000);
}

// Library: Delete document with progress tracking
export async function deleteDocumentWithProgress(fetchAPI, documentInput, onProgress = null) {
  const { documentId } = normalizeDocumentDeletionParams(documentInput);

  try {
    // Phase 1: Initiate deletion
    const deleteResult = await deleteDocument(fetchAPI, documentInput);
    let kbSyncRequestId = resolveKbSyncId(deleteResult);
    let kbJobIdForStatus = deleteResult?.kb_job_id;

    if (!deleteResult.success) {
      throw new Error('Failed to initiate deletion');
    }

    // Surface request identifiers immediately so UI can store them
    if (onProgress) {
      onProgress({
        status: deleteResult.status || 'deleting',
        progress_percent: deleteResult.progress_percent || 0,
        estimated_time_remaining: deleteResult.estimated_time_remaining,
        message: deleteResult.message,
        kb_sync_request_id: kbSyncRequestId,
        kb_job_id: kbJobIdForStatus
      });
    }

    console.log(`Deleting... (removed ${deleteResult.deleted_objects} S3 objects)`);

    // If document is already deleted (no KB sync needed), return immediately
    if (deleteResult.status === 'deleted') {
      if (onProgress) {
        onProgress({
          status: 'deleted',
          progress_percent: 100,
          message: 'Document deleted successfully',
          kb_sync_request_id: kbSyncRequestId,
          kb_job_id: kbJobIdForStatus
        });
      }
      return deleteResult;
    }

    // Phase 2: Poll for completion with exponential backoff
    const maxAttempts = 240; // Increased from 180 to allow more time for large documents
    const maxTotalWaitMs = 45 * 60 * 1000; // Increased to 45 minutes from 30 minutes
    const maxConsecutiveErrors = 5; // Maximum consecutive network errors before giving up
    let elapsedMs = 0;
    let attempts = 0;
    let consecutiveErrors = 0;
    let lastStatusSignature = null;

    while (attempts < maxAttempts) {
      attempts++;
      const pollInterval = getPollingInterval(attempts);
      elapsedMs += pollInterval;

      if (elapsedMs > maxTotalWaitMs) {
        throw new Error('Deletion timeout: exceeded maximum wait time');
      }

      await new Promise(r => setTimeout(r, pollInterval));

      let statusResult;
      try {
        statusResult = await getDocumentDeletionStatus(fetchAPI, documentId, {
          kbSyncRequestId,
          kbJobId: kbJobIdForStatus
        });
        // Reset error counter on successful fetch
        consecutiveErrors = 0;

        // Log progress every 5 attempts to help debug stuck deletions
        if (attempts % 5 === 0) {
          console.log(`[Deletion Progress] Attempt ${attempts}/${maxAttempts}:`, {
            status: statusResult?.status,
            progress: statusResult?.progress_percent,
            jobStatus: statusResult?.job_status,
            elapsed: `${Math.floor(elapsedMs / 1000)}s`,
            message: statusResult?.message,
            documentId
          });
        }

        // If status hasn't changed in 20 attempts (likely stuck), warn user
        if (attempts >= 20 && attempts % 20 === 0) {
          console.warn(`[Deletion Warning] Deletion appears stuck after ${attempts} attempts. Status:`, statusResult?.status, 'Progress:', statusResult?.progress_percent + '%');
        }
      } catch (error) {
        // Handle 404: document already fully deleted
        if (error.status === 404) {
          console.log('Document not found (404) - already deleted');
          if (onProgress) {
            onProgress({
              status: 'deleted',
              progress_percent: 100,
              message: 'Document deleted successfully',
              kb_sync_request_id: kbSyncRequestId,
              kb_job_id: kbJobIdForStatus
            });
          }
          return {
            ...deleteResult,
            status: 'deleted',
            message: 'Document deleted successfully'
          };
        }

        // Handle transient network errors with retry logic
        consecutiveErrors++;
        console.warn(`Status check failed (attempt ${consecutiveErrors}/${maxConsecutiveErrors}):`, error.message || error);

        if (consecutiveErrors >= maxConsecutiveErrors) {
          // Too many consecutive errors - give up and verify with backend
          console.error('Too many consecutive errors during deletion status polling');
          console.log('Attempting to verify deletion by checking if document still exists...');

          // Fallback: Try to fetch the document list to see if it's gone
          try {
            const documents = await fetchAPI('/api/documents');
            const stillExists = (documents.documents || documents).some(doc => {
              const docId = doc.document_id || (doc.SK ? doc.SK.replace(/^DOC#/, '') : null);
              return docId === documentId;
            });

            if (!stillExists) {
              // Document is gone from backend - deletion succeeded
              console.log('Document no longer exists in backend - deletion completed');
              if (onProgress) {
                onProgress({
                  status: 'deleted',
                  progress_percent: 100,
                  message: 'Document deleted successfully',
                  kb_sync_request_id: kbSyncRequestId,
                  kb_job_id: kbJobIdForStatus
                });
              }
              return {
                ...deleteResult,
                status: 'deleted',
                message: 'Document deleted successfully'
              };
            }

            // Document still exists but status polling is failing
            throw new Error('Deletion status polling failed repeatedly and document still exists in backend');
          } catch (verifyError) {
            // Verification also failed
            throw new Error(`Unable to verify deletion status after ${maxConsecutiveErrors} failures: ${verifyError.message || verifyError}`);
          }
        }

        // Continue polling despite error (transient network issues)
        continue;
      }

      kbSyncRequestId = resolveKbSyncId(statusResult, kbSyncRequestId);
      kbJobIdForStatus = kbJobIdForStatus || statusResult?.kb_job_id;
      const effectiveKbJobId = kbJobIdForStatus;
      const statusSignature = [
        statusResult?.status,
        statusResult?.job_status,
        statusResult?.progress_percent,
        statusResult?.estimated_time_remaining
      ].join('|');

      // Call progress callback if provided
      if (onProgress && statusSignature !== lastStatusSignature) {
        lastStatusSignature = statusSignature;
        onProgress({
          status: statusResult?.status || 'deleting',
          job_status: statusResult?.job_status,
          progress_percent: statusResult?.progress_percent || 0,
          estimated_time_remaining: statusResult?.estimated_time_remaining,
          message: statusResult?.message,
          kb_sync_request_id: kbSyncRequestId,
          kb_job_id: effectiveKbJobId
        });
      }

      // Check if deletion is complete
      if (statusResult?.status === 'deleted') {
        console.log('Deletion complete!');
        return {
          ...deleteResult,
          ...statusResult
        };
      }

      // Check for failure
      if (statusResult?.job_status === 'FAILED') {
        throw new Error(statusResult?.message || 'Deletion failed during KB sync');
      }

      // Log progress
      console.log(`Progress: ${statusResult?.progress_percent || 0}%`);
      if (statusResult?.estimated_time_remaining) {
        console.log(`Est. time remaining: ${statusResult?.estimated_time_remaining}s`);
      }
    }

    throw new Error('Deletion timeout: exceeded maximum wait time');
  } catch (error) {
    console.error('Deletion error:', error);
    throw error;
  }
}

// Reports: Poll report status until completed
export async function pollReportStatus(fetchAPI, runId, intervalMs = 15000) {
  return new Promise((resolve, reject) => {
    let intervalId;
    let timeoutId;
    let pollCount = 0;
    const startTime = Date.now();

    const checkStatus = async () => {
      try {
        pollCount++;
        const data = await fetchAPI(`/api/jobs/${runId}/status`);

        // Log status every 5 polls (~75 seconds) to track progress
        if (pollCount % 5 === 0) {
          const elapsedMin = ((Date.now() - startTime) / 60000).toFixed(1);
          console.log(`[pollReportStatus] Poll #${pollCount} at ${elapsedMin}min - Status: "${data.status}" - RunID: ${runId}`);
        }

        // Backend returns "completed" for success
        const statusLower = (data.status || '').toLowerCase();
        if (statusLower === 'completed') {
          clearInterval(intervalId);
          clearTimeout(timeoutId);
          const totalMin = ((Date.now() - startTime) / 60000).toFixed(1);
          console.log(`[pollReportStatus] ✓ Report completed in ${totalMin}min after ${pollCount} polls`);
          resolve(data);
        } else if (statusLower === 'failed') {
          clearInterval(intervalId);
          clearTimeout(timeoutId);
          console.error(`[pollReportStatus] ✗ Report failed after ${pollCount} polls:`, data.error_message || 'Unknown error');
          reject(new Error(data.error_message || 'Report generation failed'));
        }
        // Continue polling if status is 'RUNNING' or other in-progress states
      } catch (error) {
        clearInterval(intervalId);
        clearTimeout(timeoutId);
        console.error(`[pollReportStatus] Polling error after ${pollCount} attempts:`, error);
        reject(error);
      }
    };

    intervalId = setInterval(checkStatus, intervalMs);

    // Cleanup after timeout — batch jobs can take 90+ min in queue before processing starts.
    // Resolve with a sentinel rather than rejecting so the UI can show "check back later"
    // instead of marking the report as failed.
    timeoutId = setTimeout(() => {
      clearInterval(intervalId);
      const elapsedMin = ((Date.now() - startTime) / 60000).toFixed(1);
      console.warn(`[pollReportStatus] ⏱ TIMEOUT after ${elapsedMin}min (${pollCount} polls) - RunID: ${runId}`);
      resolve({ status: 'batch_timeout', run_id: runId });
    }, REPORT_POLLING_TIMEOUT_MS);

    console.log(`[pollReportStatus] Starting polling for RunID: ${runId} (timeout: ${REPORT_POLLING_TIMEOUT_MS / 60000}min, interval: ${intervalMs / 1000}s)`);
    checkStatus();
  });
}

// Reports: Fetch PDF report with retry logic for async generation
// For large reports (2900+ pairs), PDF generation can take up to 15 minutes
export async function fetchReportPDF(fetchAPI, runId, retries = 30, retryDelay = 30000, onProgress = null) {
  const maxRetries = retries;
  const startTime = Date.now();

  const response = await fetchAPI(`/api/reports/${runId}/pdf`, { responseType: 'response' });

  if (response.status === 202) {
    const data = await response.json().catch(() => ({}));
    const nextDelay = data.retry_after ? data.retry_after * 1000 : retryDelay;

    if (retries > 0) {
      const elapsedSeconds = Math.floor((Date.now() - startTime) / 1000);
      if (onProgress) {
        onProgress({
          status: data.status || 'generating',
          message: data.message || 'Generating PDF...',
          elapsedSeconds,
          attemptNumber: maxRetries - retries + 1,
          maxAttempts: maxRetries,
        });
      }
      await new Promise((r) => setTimeout(r, nextDelay));
      return fetchReportPDF(fetchAPI, runId, retries - 1, nextDelay, onProgress);
    }
    const elapsedMinutes = Math.floor((Date.now() - startTime) / 60000);
    throw new Error(
      `PDF generation timeout after ${elapsedMinutes} minutes. Please try again later.`
    );
  }

  if (!response.ok) {
    const err = await response.json().catch(() => ({}));
    throw new Error(err.error || err.message || `HTTP ${response.status}`);
  }

  return response.blob();
}

export { APIError };
